"use client"

import { useState, Fragment, useEffect } from "react"
import { Dialog, Transition } from "@headlessui/react"
import { X, DollarSign, ArrowRight, CreditCard, Wallet, RefreshCw, AlertCircle, Info } from 'lucide-react'
import { InputCustom, SearchableSelect } from "../../components"
import adAccountsService from "../../services/adAccountsService"
import transferHistoryService from "../../services/transferHistoryService"
import toast from "react-hot-toast"

const TransferBetweenAccountsModal = ({ isOpen, onClose, account, onSuccess }) => {
  const [transferAmount, setTransferAmount] = useState("")
  const [isProcessing, setIsProcessing] = useState(false)
  const [transferComplete, setTransferComplete] = useState(false)
  const [loading, setLoading] = useState(false)
  const [adAccounts, setAdAccounts] = useState([])
  const [formData, setFormData] = useState({
    sourceAccountId: "",
    targetAccountId: "",
    notes: "",
  })
  const [sourceAccount, setSourceAccount] = useState({})
  const [targetAccount, setTargetAccount] = useState({})
  const [errors, setErrors] = useState({})

  const [sourceFeePercentage, setSourceFeePercentage] = useState(0)
  const [sourceFeeAmount, setSourceFeeAmount] = useState(0)
  const [targetFeePercentage, setTargetFeePercentage] = useState(0)
  const [targetFeeAmount, setTargetFeeAmount] = useState(0)
  const [netAmountReceived, setNetAmountReceived] = useState(0)

  // Fetch available ad accounts when modal opens
  useEffect(() => {
    if (isOpen) {
      fetchAdAccounts()
      // Set source account to the current account if provided
      if (account) {
        setFormData({
          ...formData,
          sourceAccountId: account._id,
        })
      }

      // Reset states
      setTransferAmount("")
      setIsProcessing(false)
      setTransferComplete(false)
    }
  }, [isOpen, account])

  // Fetch account details when source or target account changes
  useEffect(() => {
    if (formData.sourceAccountId) {
      fetchAccountDetails(formData.sourceAccountId, "source")
    }
    if (formData.targetAccountId) {
      fetchAccountDetails(formData.targetAccountId, "target")
    }
  }, [formData.sourceAccountId, formData.targetAccountId])

  // Calculate fees when transfer amount or accounts change
  useEffect(() => {
    if (transferAmount && sourceAccount.platform && targetAccount.platform) {
      calculateFees()
    }
  }, [transferAmount, sourceAccount, targetAccount])

  // Fetch ad accounts from API
  const fetchAdAccounts = async () => {
    try {
      setLoading(true)
      const response = await adAccountsService.search({
        status: "active", // Only fetch active accounts
        pagination: {
          page: 1,
          limit: 100,
        },
      })

      if (response.data && response.data.statusCode === 200) {
        // Format accounts for dropdown
        const accounts = response.data.data.map((acc) => ({
          id: acc._id,
          name: `${acc.account_name} (${acc.currency})`,
          value: acc._id,
          currency: acc.currency,
          balance: acc.balance,
          platform: acc.platform,
          account_name: acc.account_name,
          credit_account: acc.credit_account,
        }))
        setAdAccounts(accounts)
      }
      setLoading(false)
    } catch (error) {
      console.error("Failed to fetch ad accounts:", error)
      toast.error("Failed to load ad accounts")
      setLoading(false)
    }
  }

  // Fetch account details
  const fetchAccountDetails = async (accountId, type) => {
    try {
      const response = await adAccountsService.get(accountId)
      const responseData = response.data

      if (responseData.data && responseData.statusCode === 200) {
        const accountData = {
          _id: responseData.data._id,
          platform: responseData.data.platform || "meta",
          currency: responseData.data.currency || "USD",
          credit_account: responseData.data.credit_account?._id || "",
          credit_account_name: responseData.data.credit_account?.account_name || "",
          credit_account_balance: responseData.data.credit_account?.balance || 0,
          credit_account_currency: responseData.data.credit_account?.account_currency || "",
          client: responseData.data.user?._id || "",
          clientName: responseData.data.user?.fullName || "",
          clientEmail: responseData.data.user?.email || "",
          accountName: responseData.data.account_name || "",
          accountAlias: responseData.data.account_alias || "",
          balance: responseData.data.balance || 0,
          feePercentage: responseData.data.feePercentage || 0,
        }

        if (type === "source") {
          setSourceAccount(accountData)
        } else {
          setTargetAccount(accountData)
        }
      }
    } catch (error) {
      console.error(`Failed to load ${type} account data:`, error)
    }
  }

  // Calculate fees for both source and target accounts
  const calculateFees = () => {
    const amount = Number.parseFloat(transferAmount) || 0

    const sourceFee = Number(sourceAccount.feePercentage || 0)
    const targetFee = Number(targetAccount.feePercentage || 0)

    setSourceFeePercentage(sourceFee)
    setTargetFeePercentage(targetFee)

    const calculatedSourceFee = (amount * sourceFee) / 100
    const calculatedTargetFee = (amount * targetFee) / 100

    setSourceFeeAmount(calculatedSourceFee)
    setTargetFeeAmount(calculatedTargetFee)
    setNetAmountReceived(amount - calculatedTargetFee)
  }

  // Handle input changes
  const handleChange = (field, value) => {
    setFormData({
      ...formData,
      [field]: value,
    })

    // Clear error when field is updated
    if (errors[field]) {
      setErrors({
        ...errors,
        [field]: "",
      })
    }
  }

  // Handle amount change
  const handleAmountChange = (value) => {
    const amount = value.replace(/[^0-9.]/g, "")
    setTransferAmount(amount)

    if (errors.transferAmount) {
      setErrors({
        ...errors,
        transferAmount: "",
      })
    }
  }

  // Validate form
  const validateForm = () => {
    const newErrors = {}

    if (!formData.sourceAccountId) {
      newErrors.sourceAccountId = "Source account is required"
    }

    if (!formData.targetAccountId) {
      newErrors.targetAccountId = "Target account is required"
    } else if (formData.sourceAccountId === formData.targetAccountId) {
      newErrors.targetAccountId = "Source and target accounts cannot be the same"
    }

    if (!transferAmount) {
      newErrors.transferAmount = "Transfer amount is required"
    } else if (isNaN(Number(transferAmount)) || Number(transferAmount) <= 0) {
      newErrors.transferAmount = "Please enter a valid amount greater than 0"
    } else {
      // Check if source account has sufficient balance
      const availableBalance = sourceAccount.balance || 0
      if (Number.parseFloat(transferAmount) > availableBalance) {
        newErrors.transferAmount = `Insufficient funds. Available balance: $${formatCurrency(availableBalance)}`
      }
    }

    setErrors(newErrors)
    return Object.keys(newErrors).length === 0
  }

  // Check available balance before attempting transfer
  const checkSufficientFunds = () => {
    const availableBalance = sourceAccount.balance || 0
    return Number.parseFloat(transferAmount) <= availableBalance
  }

  // Handle form submission
  const handleTransferFunds = async () => {
    if (!validateForm()) {
      return
    }

    if (!checkSufficientFunds()) {
      setErrors({
        transferAmount: `Insufficient funds. Available balance: $${formatCurrency(sourceAccount.balance || 0)}`,
      })
      return
    }

    setIsProcessing(true)

    try {
      // Prepare data for API
      const transferData = {
        sourceAccountId: formData.sourceAccountId,
        targetAccountId: formData.targetAccountId,
        amount: Number.parseFloat(transferAmount),
        sourceFee: sourceFeeAmount,
        sourceFeePercentage: sourceFeePercentage,
        targetFee: targetFeeAmount,
        targetFeePercentage: targetFeePercentage,
        netAmountReceived: netAmountReceived,
        notes: formData.notes || "Fund transfer",
      }

      const response = await transferHistoryService.transferBetweenAccounts(transferData)

      if (response.data && response.data.statusCode === 200) {
        setIsProcessing(false)
        setTransferComplete(true)
        toast.success("Funds transferred successfully")

        setTimeout(() => {
          if (onSuccess) {
            onSuccess()
          }
          onClose()
        }, 1500)
      }
    } catch (error) {
      console.error("Fund transfer failed:", error)
      setIsProcessing(false)
      setErrors({
        api: error.response?.data?.message || "Failed to transfer funds",
      })
      toast.error(error.response?.data?.message || "Failed to transfer funds")
    }
  }

  // Format currency
  const formatCurrency = (value) => {
    return new Intl.NumberFormat("en-US", {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    }).format(value)
  }

  // Get platform display name
  const getPlatformDisplayName = (platform) => {
    switch (platform) {
      case "meta":
        return "Meta"
      case "google":
        return "Google"
      case "tiktok":
        return "TikTok"
      case "snapchat":
        return "Snapchat"
      default:
        return platform.charAt(0).toUpperCase() + platform.slice(1)
    }
  }

  return (
    <Transition appear show={isOpen} as={Fragment}>
      <Dialog as="div" className="relative z-50" onClose={onClose}>
        <Transition.Child
          as={Fragment}
          enter="ease-out duration-300"
          enterFrom="opacity-0"
          enterTo="opacity-100"
          leave="ease-in duration-200"
          leaveFrom="opacity-100"
          leaveTo="opacity-0"
        >
          <div className="fixed inset-0 bg-black/60 backdrop-blur-sm" />
        </Transition.Child>

        <div className="fixed inset-0 overflow-y-auto">
          <div className="flex min-h-full items-center justify-center p-4">
            <Transition.Child
              as={Fragment}
              enter="ease-out duration-300"
              enterFrom="opacity-0 scale-95"
              enterTo="opacity-100 scale-100"
              leave="ease-in duration-200"
              leaveFrom="opacity-100 scale-100"
              leaveTo="opacity-0 scale-95"
            >
              <Dialog.Panel className="w-full max-w-md transform overflow-hidden rounded-xl bg-white dark:bg-neutral-950 shadow-2xl transition-all border border-neutral-200 dark:border-neutral-900 dark:bg-neutral-900/30">
                {/* Simple header with neutral background */}
                <div className="relative border-b border-neutral-200 dark:border-neutral-900 dark:bg-neutral-900/30 p-5">
                  <div className="flex items-center justify-between">
                    <Dialog.Title className="text-xl font-[400] text-neutral-900 dark:text-white">
                      Transfer Between Accounts
                    </Dialog.Title>
                    <button
                      onClick={onClose}
                      className="p-1.5 rounded-full text-neutral-500 hover:bg-neutral-100 dark:text-neutral-400 dark:hover:bg-neutral-800 transition-colors"
                      type="button"
                      aria-label="Close"
                    >
                      <X size={20} />
                    </button>
                  </div>
                </div>

                <div className="p-6">
                  {loading ? (
                    <div className="flex items-center justify-center py-12">
                      <RefreshCw size={30} className="animate-spin text-neutral-600 dark:text-neutral-400" />
                    </div>
                  ) : (
                    <>
                      {transferComplete ? (
                        <div className="flex flex-col items-center justify-center py-6">
                          <div className="w-16 h-16 rounded-full bg-green-100 dark:bg-green-900/30 flex items-center justify-center mb-4">
                            <svg
                              className="w-8 h-8 text-green-600 dark:text-green-400"
                              fill="none"
                              viewBox="0 0 24 24"
                              stroke="currentColor"
                            >
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                            </svg>
                          </div>
                          <h3 className="text-lg font-medium text-neutral-900 dark:text-white mb-1">
                            Transfer Complete
                          </h3>
                          <p className="text-neutral-500 dark:text-neutral-400 text-center">
                            ${formatCurrency(netAmountReceived)} has been successfully transferred
                          </p>
                          <div className="mt-3 text-sm text-neutral-500 dark:text-neutral-400">
                            <span>From: </span>
                            <span className="font-medium text-neutral-700 dark:text-neutral-300">
                              {sourceAccount.accountName || "Source Account"}
                            </span>
                          </div>
                          <div className="text-sm text-neutral-500 dark:text-neutral-400">
                            <span>To: </span>
                            <span className="font-medium text-neutral-700 dark:text-neutral-300">
                              {targetAccount.accountName || "Target Account"}
                            </span>
                          </div>
                        </div>
                      ) : (
                        <>
                          {/* Account Selection */}
                          {!account && (
                            <div className="mb-6">
                              <label className="block mb-2 text-xs font-medium text-neutral-800 dark:text-neutral-200">
                                Source Account
                              </label>
                              <SearchableSelect
                                options={adAccounts}
                                value={formData.sourceAccountId}
                                onChange={(value) => handleChange("sourceAccountId", value)}
                                placeholder="Select source account"
                                icon={<CreditCard size={16} />}
                                error={errors.sourceAccountId}
                              />
                              {errors.sourceAccountId && (
                                <p className="text-xs text-red-500 mt-1 flex items-center gap-1">
                                  <AlertCircle size={12} />
                                  {errors.sourceAccountId}
                                </p>
                              )}
                            </div>
                          )}

                          <div className="mb-6">
                            <label className="block mb-2 text-xs font-medium text-neutral-800 dark:text-neutral-200">
                              Target Account
                            </label>
                            <SearchableSelect
                              options={adAccounts.filter((acc) => acc.value !== formData.sourceAccountId)}
                              value={formData.targetAccountId}
                              onChange={(value) => handleChange("targetAccountId", value)}
                              placeholder="Select target account"
                              icon={<CreditCard size={16} />}
                              error={errors.targetAccountId}
                            />
                            {errors.targetAccountId && (
                              <p className="text-xs text-red-500 mt-1 flex items-center gap-1">
                                <AlertCircle size={12} />
                                {errors.targetAccountId}
                              </p>
                            )}
                          </div>

                          {/* Transfer cards */}
                          {formData.sourceAccountId && formData.targetAccountId && (
                            <div className="flex flex-col md:flex-row gap-4 mb-8 relative">
                              {/* From card */}
                              <div className="flex-1 rounded-xl border border-neutral-200 dark:border-neutral-900 dark:bg-neutral-900/30 bg-white dark:border-neutral-900 dark:bg-neutral-900/30 shadow-sm overflow-hidden">
                                <div className="bg-neutral-100 dark:bg-neutral-700 px-4 py-2 flex items-center gap-2">
                                  <CreditCard size={16} className="text-neutral-500 dark:text-neutral-400" />
                                  <span className="text-sm font-medium text-neutral-700 dark:text-neutral-200">
                                    Transfer From
                                  </span>
                                </div>
                                <div className="p-4">
                                  <div className="font-medium text-neutral-800 dark:text-white truncate mb-3">
                                    {sourceAccount.accountName || "Source Account"}
                                    {sourceAccount.platform && (
                                      <span className="ml-1 text-xs text-neutral-500 dark:text-neutral-400">
                                        ({getPlatformDisplayName(sourceAccount.platform)})
                                      </span>
                                    )}
                                  </div>
                                  <div className="text-xs text-neutral-500 dark:text-neutral-400">Current Balance</div>
                                  <div className="text-lg font-[600] text-neutral-900 dark:text-white">
                                    ${formatCurrency(sourceAccount.balance || 0)}
                                  </div>
                                </div>
                              </div>

                              {/* Arrow between cards */}
                              <div className="absolute left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2 hidden md:flex">
                                <div className="w-10 h-10 rounded-full bg-neutral-100 dark:bg-neutral-700 shadow-md flex items-center justify-center">
                                  <ArrowRight size={18} className="text-neutral-600 dark:text-neutral-300" />
                                </div>
                              </div>

                              {/* Mobile arrow */}
                              <div className="flex justify-center md:hidden -my-2">
                                <div className="w-8 h-8 rounded-full bg-neutral-100 dark:bg-neutral-700 shadow-md flex items-center justify-center">
                                  <ArrowRight size={16} className="text-neutral-600 dark:text-neutral-300 rotate-90" />
                                </div>
                              </div>

                              {/* To card */}
                              <div className="flex-1 rounded-xl border border-neutral-200 dark:border-neutral-900 dark:bg-neutral-900/30 bg-neutral-50 dark:border-neutral-900 dark:bg-neutral-900/30 shadow-sm overflow-hidden">
                                <div className="bg-neutral-100 dark:bg-neutral-700 px-4 py-2 flex items-center gap-2">
                                  <Wallet size={16} className="text-neutral-500 dark:text-neutral-400" />
                                  <span className="text-sm font-medium text-neutral-700 dark:text-neutral-200">
                                    Transfer To
                                  </span>
                                </div>
                                <div className="p-4">
                                  <div className="font-medium text-neutral-800 dark:text-white truncate mb-3">
                                    {targetAccount.accountName || "Target Account"}
                                    {targetAccount.platform && (
                                      <span className="ml-1 text-xs text-neutral-500 dark:text-neutral-400">
                                        ({getPlatformDisplayName(targetAccount.platform)})
                                      </span>
                                    )}
                                  </div>
                                  <div className="text-xs text-neutral-500 dark:text-neutral-400">Current Balance</div>
                                  <div className="text-lg font-[600] text-neutral-900 dark:text-white">
                                    ${formatCurrency(targetAccount.balance || 0)}
                                  </div>
                                </div>
                              </div>
                            </div>
                          )}

                          {/* Fee Information */}
                          {(sourceFeePercentage > 0 || targetFeePercentage > 0) && formData.sourceAccountId && formData.targetAccountId && (
                            <div className="mb-4 p-3 bg-blue-50 dark:bg-blue-900/20 rounded-xl border border-blue-100 dark:border-blue-800/30 flex items-start gap-2">
                              <Info className="text-blue-500 dark:text-blue-400 mt-0.5 flex-shrink-0" size={16} />
                              <div>
                                <p className="text-xs text-blue-800 dark:text-blue-300">
                                  Source account will receive back {sourceFeePercentage}% fees. Target account will pay {targetFeePercentage}% fees.
                                </p>
                              </div>
                            </div>
                          )}

                          {/* Transfer Amount */}
                          <div className="mb-6">
                            <InputCustom
                              label="Transfer Amount"
                              placeholder="Enter Transfer Amount"
                              type="number"
                              value={transferAmount}
                              onChange={(value) => handleAmountChange(value)}
                              icon={<DollarSign size={18} className="text-neutral-500 dark:text-neutral-400" />}
                              error={errors.transferAmount}
                            />
                            {errors.transferAmount && (
                              <p className="text-xs text-red-500 mt-1 flex items-center gap-1">
                                <AlertCircle size={12} />
                                {errors.transferAmount}
                              </p>
                            )}
                          </div>

                          {/* Notes */}
                          <div className="mb-6">
                            <InputCustom
                              label="Notes (Optional)"
                              placeholder="Enter notes for this transfer"
                              value={formData.notes}
                              onChange={(value) => handleChange("notes", value)}
                              icon={<Info size={16} className="text-neutral-500 dark:text-neutral-400" />}
                            />
                          </div>

                          {/* Transfer Amount Display */}
                          {transferAmount && (
                            <div className="mb-6 space-y-3 p-4 rounded-xl bg-neutral-50 dark:border-neutral-900 dark:bg-neutral-900/50 border border-neutral-200 dark:bg-neutral-800">
                              <div className="flex justify-between">
                                <span className="text-sm text-neutral-600 dark:text-neutral-400">Transfer Amount</span>
                                <span className="text-sm text-neutral-800 dark:text-neutral-200">
                                  ${formatCurrency(Number.parseFloat(transferAmount) || 0)}
                                </span>
                              </div>

                              {sourceFeePercentage > 0 && (
                                <div className="flex justify-between">
                                  <span className="text-sm text-green-600 dark:text-green-400">
                                    + Source Fees Returned ({sourceFeePercentage}%)
                                  </span>
                                  <span className="text-sm text-green-600 dark:text-green-400">
                                    +${formatCurrency(sourceFeeAmount)}
                                  </span>
                                </div>
                              )}

                              {targetFeePercentage > 0 && (
                                <div className="flex justify-between">
                                  <span className="text-sm text-red-600 dark:text-red-400">
                                    - Target Fees ({targetFeePercentage}%)
                                  </span>
                                  <span className="text-sm text-red-600 dark:text-red-400">
                                    -${formatCurrency(targetFeeAmount)}
                                  </span>
                                </div>
                              )}

                              <div className="pt-2 border-t border-neutral-200 dark:bg-neutral-800 flex justify-between">
                                <span className="text-sm font-medium text-neutral-800 dark:text-neutral-200">
                                  Source Account Change
                                </span>
                                <span className="text-sm font-[600] text-neutral-600 dark:text-neutral-400">
                                  ${formatCurrency((Number.parseFloat(transferAmount) || 0) - sourceFeeAmount)}
                                </span>
                              </div>

                              <div className="flex justify-between">
                                <span className="text-sm font-medium text-neutral-800 dark:text-neutral-200">
                                  Target Account Receives
                                </span>
                                <span className="text-sm font-[600] text-blue-600 dark:text-blue-400">
                                  ${formatCurrency(netAmountReceived)}
                                </span>
                              </div>

                              <div className="pt-2 border-t border-neutral-200 dark:bg-neutral-800 flex justify-between">
                                <span className="text-xs text-neutral-500 dark:text-neutral-400">Currency</span>
                                <span className="text-xs font-medium text-neutral-700 dark:text-neutral-300">
                                  {sourceAccount.currency || "USD"}
                                </span>
                              </div>
                            </div>
                          )}

                          {/* Transfer Button */}
                          <button
                            type="button"
                            className="w-full h-11 px-4 bg-neutral-900 text-white dark:bg-neutral-100 dark:text-black rounded-xl hover:scale-105 transition-all duration-400 flex items-center justify-center text-sm font-[400] disabled:opacity-70 disabled:cursor-not-allowed"
                            onClick={handleTransferFunds}
                            disabled={
                              isProcessing ||
                              !transferAmount ||
                              Number.parseFloat(transferAmount) <= 0 ||
                              !formData.sourceAccountId ||
                              !formData.targetAccountId
                            }
                          >
                            {isProcessing ? (
                              <>
                                <RefreshCw size={18} className="animate-spin mr-2" />
                                Processing...
                              </>
                            ) : (
                              "Transfer Funds"
                            )}
                          </button>
                        </>
                      )}
                    </>
                  )}
                  {errors.api && (
                    <div className="mt-4 p-3 bg-red-50 dark:bg-red-900/20 border border-red-100 dark:border-red-800/30 rounded-xl flex items-start gap-2">
                      <AlertCircle className="text-red-500 dark:text-red-400 mt-0.5 flex-shrink-0" size={16} />
                      <span className="text-xs text-red-800 dark:text-red-300">{errors.api}</span>
                    </div>
                  )}
                </div>
              </Dialog.Panel>
            </Transition.Child>
          </div>
        </div>
      </Dialog>
    </Transition>
  )
}

export default TransferBetweenAccountsModal